import { useTranslations } from 'next-intl';
import { Conditional } from '@cat-ecom/pcc-components';
import {
  CatGrid,
  CatGridItem,
  CatIconCustomer,
  CatIconShipping,
  CatSkeleton
} from 'components/blocks-components';
import { InterimStoreAvailabilityType } from '../types';
import { getAvailabilityPickupText } from 'components/availability-wrapper/utils';
import StoreAvailabilityByInStock from './components/store-availability-by-instock';
import StoreAvailabilityByDate from './components/store-availability-by-date';
import StoreAvailabilityContactDealer from './components/store-availability-contact-dealer';
import EstimatedDeliveryDate from './estimated-delivery-date/estimated-delivery-date';
import ShowPickupTime from './show-pickup-time/show-pickup-time';
import styles from './store-availability.module.scss';
import cx from 'classnames';

const InterimStoreAvailablityGrid = ({
  isLoading,
  isDeliveryEnabled,
  isPickupEnabled,
  address,
  locale,
  storeAvailability,
  showPickupCutoffFlag,
  pdpEstimatedDeliveryDateFlag,
  isLoggedIn,
  cutoffTimesData,
  deliveryAvailability,
  availabilityByStoreLink,
  pdpHeaderUpdateMobileViewFlag = false,
  isMD = false
}: InterimStoreAvailabilityType) => {
  const t = useTranslations();
  const commonStyle = `text-uppercase fw-bold cat-u-padding-left-sm cat-u-margin-bottom-none  ${styles['storeAvailablity__text-color']}`;
  const textStyle = `fw-normal cat-u-padding-left-sm cat-u-margin-bottom-none   ${styles['storeAvailablity__font-style']}`;
  const titleStyle = `fw-bold cat-u-padding-left-sm cat-u-margin-bottom-none   ${styles['storeAvailablity__text-style']}`;
  const { inStock, contactDealer, byDate } = storeAvailability;
  const showDeliveryText = inStock === null && byDate === null;

  const getAvailabilityTable = () => {
    return (
      (byDate || contactDealer || inStock) && (
        <div
          className={cx(
            'cat-u-padding-md',
            'border',
            'cat-u-theme-typography-body-sm',
            styles['storeAvailablity__section'],
            {
              [styles['storeAvailablity__section--mobile']]:
                pdpHeaderUpdateMobileViewFlag
            }
          )}
        >
          <CatGrid gap="none">
            <CatGrid variant="side-by-side">
              <CatGridItem className="border-end">
                <div className="d-flex align-items-lg-center align-items-start mb-1">
                  <Conditional
                    test={pdpHeaderUpdateMobileViewFlag && !isMD}
                    fallback={<CatIconCustomer />}
                  >
                    <CatIconCustomer size="xl" color="content-strong" />
                  </Conditional>
                  <Conditional
                    test={pdpHeaderUpdateMobileViewFlag && !isMD}
                    fallback={
                      <p className={commonStyle}>{t('PDP_ESTIMATED_PICKUP')}</p>
                    }
                  >
                    <div className="d-flex flex-column">
                      <p className={textStyle}>{t('ESTIMATED')}</p>
                      <p className={titleStyle}>
                        {t('CAT_SHOPPING_PREFERENCES_PICKUP')}
                      </p>
                    </div>
                  </Conditional>
                </div>
                <table>
                  <tbody>
                    {inStock && (
                      <StoreAvailabilityByInStock inStock={inStock} />
                    )}
                    {byDate && (
                      <StoreAvailabilityByDate
                        byDate={byDate}
                        contactDealer={contactDealer}
                        locale={locale}
                      />
                    )}
                    {showPickupCutoffFlag && !showDeliveryText && (
                      <ShowPickupTime
                        cutoffTime={cutoffTimesData?.pickup}
                        timezone={cutoffTimesData?.timezone}
                      />
                    )}
                  </tbody>
                </table>
              </CatGridItem>
              <CatGridItem>
                <div className="d-flex align-items-lg-center align-items-start mb-1">
                  <Conditional
                    test={pdpHeaderUpdateMobileViewFlag && !isMD}
                    fallback={<CatIconShipping />}
                  >
                    <CatIconShipping size="xl" color="content-strong" />
                  </Conditional>
                  <Conditional
                    test={pdpHeaderUpdateMobileViewFlag && !isMD}
                    fallback={
                      <p className={commonStyle}>
                        {t('PDP_ESTIMATED_DELIVERY')}
                      </p>
                    }
                  >
                    <div className="d-flex flex-column">
                      <p className={textStyle}>{t('ESTIMATED')}</p>
                      <p className={titleStyle}>{t('DELIVERY')}</p>
                    </div>
                  </Conditional>
                </div>

                {!showDeliveryText && !isLoggedIn && (
                  <p className="fst-italic">
                    {t(
                      getAvailabilityPickupText(
                        isDeliveryEnabled,
                        isPickupEnabled
                      )
                    )}
                  </p>
                )}
                {pdpEstimatedDeliveryDateFlag &&
                  isLoggedIn &&
                  deliveryAvailability && (
                    <EstimatedDeliveryDate
                      deliveryAvailability={deliveryAvailability}
                      locale={locale}
                      cutoffTimesData={cutoffTimesData}
                    />
                  )}
              </CatGridItem>
            </CatGrid>
            {contactDealer && (
              <StoreAvailabilityContactDealer
                contactDealer={contactDealer}
                address={address}
              />
            )}
            <div className="mt-2">{availabilityByStoreLink}</div>
          </CatGrid>
        </div>
      )
    );
  };

  return (
    <div
      data-testid="interim-store-availability-grid"
      className="cat-u-margin-top-md"
    >
      <Conditional
        test={!isLoading}
        fallback={<CatSkeleton data-testid="loader-id" width="300px" />}
      >
        {getAvailabilityTable()}
      </Conditional>
    </div>
  );
};

export default InterimStoreAvailablityGrid;
